import { KeyValueDefault } from '@/main/models/common';
import { ReassignPartsCancelledRequest } from '@/main/models/request';
import {
  GetItemsCancelledBySupplierResponse,
  ReassignBodyshopProcurementSupplierItemsResponse
} from '@/main/models/response';
import { getTranslateByKey, isNullUndefinedEmpty } from '@/main/services/utils';
import { currencyToNumber } from '@/main/services/utilsNumber';
import { convertStringForBoolean } from '@/main/services/utilsString';
import z from 'zod';

const requiredMsg = (fieldKey: string) =>
  getTranslateByKey('information.isRequired', true).replace(
    '{0}',
    getTranslateByKey(fieldKey)
  );

const keyValueSchema = z.custom<KeyValueDefault>();

const requiredKeyValue = (fieldKey: string) =>
  keyValueSchema.nullable().refine((v): v is KeyValueDefault => v !== null, {
    error: () => requiredMsg(fieldKey)
  });

const cancelledPartReassignBodyshopSchema = z.object({
  guidProcurementSupplierItem: z.string(),
  price: z.string().refine(v => !isNullUndefinedEmpty(v), {
    error: () => requiredMsg('information.price')
  }),
  partType: requiredKeyValue('information.partTypes'),
  brand: z.custom<KeyValueDefault>().nullable()
});

export type ICancelledPartReassignBodyshopSchema = z.input<
  typeof cancelledPartReassignBodyshopSchema
>;

export const reassignBodyshopPartsCancelledSchema = z
  .object({
    parts: z.array(cancelledPartReassignBodyshopSchema),
    reason: requiredKeyValue('information.reason'),
    reasonDescription: z.string().nullable()
  })
  .superRefine((data, ctx) => {
    if (
      data.reason &&
      convertStringForBoolean(data.reason.default) &&
      !data.reasonDescription?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['reasonDescription'],
        message: requiredMsg('information.description')
      });
    }
  });

export type ReassignBodyshopPartsCancelledForm = z.input<
  typeof reassignBodyshopPartsCancelledSchema
>;
export type ReassignBodyshopPartsCancelledFormOutput = z.output<
  typeof reassignBodyshopPartsCancelledSchema
>;

export const INITIAL_VALUES_REASSIGN_BODYSHOP_CANCELLED: ReassignBodyshopPartsCancelledForm =
  {
    parts: [],
    reason: null,
    reasonDescription: null
  };

export function buildReassignBodyshopRequest(
  data: ReassignBodyshopPartsCancelledFormOutput
): ReassignPartsCancelledRequest[] {
  return data.parts.map(part => ({
    guidProcurementSupplierItem: part.guidProcurementSupplierItem,
    guidReason: String(data.reason?.key),
    reasonDescription: data.reasonDescription
      ? String(data.reasonDescription)
      : undefined,
    newPrice: part.price
      ? (currencyToNumber(part.price) ?? undefined)
      : undefined,
    idNewPartType: Number(part.partType.default),
    guidNewPartBrand: part.brand ? String(part.brand.key) : undefined
  }));
}

export function getUnelegibleGuids(
  result?: ReassignBodyshopProcurementSupplierItemsResponse | null
): Set<string> {
  return new Set(result?.guidUnelegilbleProcurementSupplierItems ?? []);
}

export function filterEligibleRequest(
  request: ReassignPartsCancelledRequest[],
  unelegibleGuids: Set<string>
) {
  return request.filter(
    item => !unelegibleGuids.has(item.guidProcurementSupplierItem)
  );
}

export function mapPartsToFormValues(
  parts: GetItemsCancelledBySupplierResponse[]
): ICancelledPartReassignBodyshopSchema[] {
  return parts.map(part => ({
    guidProcurementSupplierItem: part.procurementSupplierItemGuid,
    partType: null,
    brand: null,
    price: ''
  }));
}
